import { useState } from "react";
import type { ReactNode, RefObject } from "react";
import { AlertTriangle } from "lucide-react";
import { MGR_ROLES } from "@hrms/types";
import { uploadDocumentWithAI, UploadZone } from "../../../lib/resumeUpload.js";
import { DocVersionHistory } from "../../DocVersionHistory.js";
import { DatePicker } from "../../DatePicker.js";
import type { LightboxTarget } from "../../DocumentLightbox.js";
import type { LookupOption } from "../../../context/LookupContext.js";
import { AsyncButton } from "../../ui/async-button.js";
import { Input } from "../../ui/input.js";
import { Label } from "../../ui/label.js";
import { SelectField } from "../../ui/select-field.js";
import { Checkbox } from "../../ui/checkbox.js";
import { Tooltip } from "../../ui/tooltip.js";
import { inp, Field, FieldGrid, CollapsibleSection } from "../sectionShared.js";
import { NewHireIntakeRow, type IntakeDocument } from "./NewHireIntakeRow.js";
import { NewHireCreatedPanel, type OnboardingLinkRecord } from "./NewHireCreatedPanel.js";

type InputProps = { value: string; onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void; disabled?: boolean; style: React.CSSProperties };
type DatePickerFieldProps = { value: string; onChange: (v: string) => void; disabled?: boolean; style: React.CSSProperties };

export interface Department { id: string; name: string; code: string; office: { id: string; name: string } | null }
export interface Office     { id: string; name: string; city: string; country: string }
export interface ManagerOption { id: string; firstName: string; lastName: string; employeeCode: string; role: string; officeId: string | null; department?: { name: string } | null }

// Matched by name: every office has its own HR department, so no single id covers them.
const HR_DEPT_PATTERN = /\b(hr|human resources?|recruit(ment|ing|er)?|talent acquisition)\b/i;
const inHrDept = (m: ManagerOption) => HR_DEPT_PATTERN.test(m.department?.name ?? "");
export interface OrgMeta   { offices: Office[]; departments: Department[] }

interface NameMismatch { sources: [string, string][] }

// Everything the create form needs beyond the org fields this section already
// owns. Passed only in create mode: its presence is what turns the section into
// the "start here" intake card, so edit mode renders exactly as before.
export interface NewHireIntake {
  offerLetter: IntakeDocument;
  resume: IntakeDocument;
  /** e.g. "Offer letter states ₹18,00,000 CTC — enter it in Compensation." */
  ctcNote: string;
  personalEmail: string; setPersonalEmail: (v: string) => void;
  /** Field labels still blank, used to explain why Save is disabled. */
  missing: string[];
  onSave: () => Promise<unknown>;
  /** Set once the create POST has succeeded. */
  created: {
    employeeId: string;
    employeeCode: string;
    record: OnboardingLinkRecord | null;
    onLinkSent: () => Promise<unknown>;
    onOpenProfile: () => void;
  } | null;
}

interface Props {
  sectionRef: RefObject<HTMLDivElement>;
  canView: (field: string) => boolean;
  inputProps: (field: string, value: string, onChange: (v: string) => void) => InputProps;
  datePickerProps: (field: string, value: string, onChange: (v: string) => void) => DatePickerFieldProps;
  approvalNote: (field: string) => ReactNode;
  pendingBadge: (field: string) => ReactNode;
  isCreate: boolean;
  isHR: boolean;
  employeeType: string; setEmployeeType: (v: string) => void;
  employeeTypes: string[];
  role: string; setRole: (v: string) => void;
  roles: string[];
  designation: string; setDesignation: (v: string) => void;
  officeId: string; setOfficeId: (v: string) => void;
  departmentId: string; setDepartmentId: (v: string) => void;
  orgMeta: OrgMeta;
  managerId: string; setManagerId: (v: string) => void;
  reportingManagerId: string; setReportingManagerId: (v: string) => void;
  sameAsHR: boolean; setSameAsHR: (v: boolean) => void;
  managers: ManagerOption[];
  workLocation: string; setWorkLocation: (v: string) => void;
  workLocations: LookupOption[];
  hireDate: string; setHireDate: (v: string) => void;
  probationEndDate: string; setProbationEndDate: (v: string) => void;

  // Create-mode name/email row + OCR conflict banners
  firstName: string; setFirstName: (v: string) => void;
  lastName: string; setLastName: (v: string) => void;
  setMiddleName: (v: string) => void;
  email: string; setEmail: (v: string) => void;
  /** Create mode: typed by HR, never generated (INT-IN-<number>). */
  employeeCode: string; setEmployeeCode: (v: string) => void;
  emailSuggested: boolean; setEmailSuggested: (v: boolean) => void;
  nameMismatch: NameMismatch | null;
  ackedConflicts: Set<string>; setAckedConflicts: (updater: (prev: Set<string>) => Set<string>) => void;
  intake?: NewHireIntake;

  // Offer letter (edit mode — in create it is an intake slot above)
  employeeId: string | null;
  docRefreshKeys: Record<string, number>;
  setVerifyTarget: (t: LightboxTarget) => void;
  bumpDocRefresh: (type: string) => void;
  /** HRMS-0633: edit-mode offer-letter re-upload runs the AI. The parsed field
   *  set bubbles up to the parent (EmployeeProfilePage) so the compensation
   *  card's CTC prefill picks up the new annual figure. */
  onOfferLetterParsed?: (fields: Record<string, unknown>) => void;
}

export function RoleOrgSection({
  sectionRef, canView, inputProps, datePickerProps, approvalNote, pendingBadge, isCreate, isHR,
  employeeType, setEmployeeType, employeeTypes, role, setRole, roles, designation, setDesignation,
  officeId, setOfficeId, departmentId, setDepartmentId, orgMeta,
  managerId, setManagerId, reportingManagerId, setReportingManagerId, sameAsHR, setSameAsHR, managers,
  workLocation, setWorkLocation, workLocations, hireDate, setHireDate, probationEndDate, setProbationEndDate,
  firstName, setFirstName, lastName, setLastName, setMiddleName, email, setEmail, employeeCode, setEmployeeCode,
  emailSuggested, setEmailSuggested, nameMismatch, ackedConflicts, setAckedConflicts, intake,
  employeeId, docRefreshKeys, setVerifyTarget, bumpDocRefresh,
  onOfferLetterParsed,
}: Props) {
  const [sameOfficeOnly, setSameOfficeOnly] = useState(false);
  const [managersOnly, setManagersOnly] = useState(false);

  function passesFilter(m: ManagerOption): boolean {
    if (sameOfficeOnly && officeId && m.officeId !== officeId) return false;
    if (managersOnly && !MGR_ROLES.has(m.role)) return false;
    return true;
  }
  // Each picker always keeps its own currently-selected candidate visible even
  // if a filter would otherwise exclude them, so turning a filter on never
  // silently blanks out an existing selection.
  const hrManagerOptions = managers.filter(m => (inHrDept(m) && passesFilter(m)) || m.id === managerId);
  const reportingManagerOptions = managers.filter(m => passesFilter(m) || m.id === (sameAsHR ? managerId : reportingManagerId));

  return (
    <CollapsibleSection id="role" sectionRef={sectionRef} title={isCreate ? "Start here — new hire" : "Organization & Role"}>

      {/* Offer letter + résumé, read by AI to prefill everything below. */}
      {intake && <NewHireIntakeRow offerLetter={intake.offerLetter} resume={intake.resume} ctcNote={intake.ctcNote} />}

      {/* Name + Email — top row (create mode) */}
      {isCreate && (
        <FieldGrid cols="auto" gap="md" className="mb-4">
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor="new-hire-employee-code">Employee Code *</Label>
            <Input
              id="new-hire-employee-code"
              value={employeeCode}
              onChange={(e) => setEmployeeCode(e.target.value.toUpperCase())}
              placeholder="INT-IN-997"
              autoComplete="off"
            />
            <span className="text-[11px] leading-snug text-surface-500">Enter manually, e.g. INT-IN-997. Must be unique.</span>
          </div>
          <Field label="First Name *">
            <input {...inputProps("firstName", firstName, setFirstName)} autoFocus />
          </Field>
          <Field label="Last Name *">
            <input {...inputProps("lastName", lastName, setLastName)} />
          </Field>
          <Field label={emailSuggested ? "Work Email * (suggested)" : "Work Email *"}>
            <input
              type="email"
              value={email}
              style={emailSuggested ? { ...inp, borderColor: "var(--color-info)" } : inp}
              onChange={(e) => { setEmail(e.target.value); setEmailSuggested(false); }}
              placeholder="firstname.lastname@domain.com"
            />
          </Field>
          {/* Lives here rather than in Personal Info because the onboarding link
              is sent to it, and that send happens from this card. */}
          {intake && (
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor="new-hire-personal-email">Personal Email</Label>
              <Input
                id="new-hire-personal-email"
                type="email"
                value={intake.personalEmail}
                onChange={(e) => intake.setPersonalEmail(e.target.value)}
                placeholder="personal@example.com"
              />
              <span className="text-[11px] leading-snug text-surface-500">The onboarding link is sent here.</span>
            </div>
          )}
        </FieldGrid>
      )}

      {/* Name mismatch alert */}
      {isCreate && nameMismatch && !ackedConflicts.has("name_mismatch") && (
        <div style={{ marginBottom: "var(--space-4)", padding: "10px 14px", borderRadius: "var(--radius-md)", fontSize: 13,
          background: "var(--color-warning-bg)",
          border: "1px solid var(--color-warning-border)",
          color: "var(--color-warning-fg)",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <strong style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <AlertTriangle size={14} style={{ color: "var(--color-warning)" }} />
              Full Name mismatch — choose the correct name:
            </strong>
            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {nameMismatch.sources.map(([src, val]) => (
                <button
                  key={src}
                  onClick={() => {
                    const parts = val.trim().split(/\s+/).filter(Boolean);
                    if (parts[0]) setFirstName(parts[0]);
                    setMiddleName(parts.length > 2 ? parts.slice(1, -1).join(" ") : "");
                    if (parts.length > 1) setLastName(parts[parts.length - 1]!);
                    setAckedConflicts(prev => new Set([...prev, "name_mismatch"]));
                  }}
                  style={{ padding: "4px 10px", fontSize: 12, borderRadius: "var(--radius-sm)",
                    background: "var(--color-surface-0)", border: "1px solid currentColor",
                    cursor: "pointer", fontWeight: 600, color: "inherit" }}
                >
                  Use {src}: {val}
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => setAckedConflicts(prev => new Set([...prev, "name_mismatch"]))}
            style={{ background: "none", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
      )}

      <FieldGrid cols="auto" gap="md">
        {canView("employeeType") && (
          <Field label="Employee Type *">
            <SelectField {...inputProps("employeeType", employeeType, setEmployeeType)}>
              {employeeTypes.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </SelectField>
          </Field>
        )}
        {canView("role") && (
          <Field label={<>System Role *{approvalNote("role")}</>}>
            <SelectField {...inputProps("role", role, setRole)} disabled={!isCreate}>
              {roles.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
            </SelectField>
            {!isCreate && <p style={{ fontSize: 11, color: "var(--color-surface-500)", marginTop: 4 }}>Change role via Admin → Role Assignment</p>}
            {pendingBadge("role")}
          </Field>
        )}
        {canView("designation") && (
          <Field label="Designation *">
            <input {...inputProps("designation", designation, setDesignation)} placeholder="e.g. Software Engineer" />
            {pendingBadge("designation")}
          </Field>
        )}
        {canView("officeId") && (
          <Field label="Office *">
            <SelectField {...inputProps("officeId", officeId, (v) => {
              setOfficeId(v);
              const dept = orgMeta.departments.find((d) => d.id === departmentId);
              if (dept && dept.office?.id !== v) setDepartmentId("");
            })}>
              <option value="">Select office…</option>
              {(isCreate
                ? orgMeta.offices.filter(o => (o.country ?? "").toUpperCase() !== "US")
                : orgMeta.offices
              ).map((o) => <option key={o.id} value={o.id}>{o.city} — {o.name}</option>)}
            </SelectField>
            {isCreate && (
              <span style={{ fontSize: 11, color: "var(--color-surface-400)", marginTop: 2, display: "block" }}>
                US offices are hidden — US employees are managed via ADP
              </span>
            )}
          </Field>
        )}
        {canView("departmentId") && (() => {
          const departmentSelectProps = inputProps("departmentId", departmentId, setDepartmentId);
          return (
            <Field label="Department *">
              <SelectField
                {...departmentSelectProps}
                disabled={departmentSelectProps.disabled || !officeId}
              >
                <option value="">{officeId ? "Select department…" : "Select office first"}</option>
                {orgMeta.departments
                  .filter((d) => !officeId || d.office?.id === officeId)
                  .map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </SelectField>
            </Field>
          );
        })()}
        {(canView("managerId") || canView("reportingManagerId")) && (
          <div className="col-span-full flex flex-wrap items-center gap-4 rounded-md border border-surface-100 bg-surface-50 px-3 py-2">
            <span className="text-xs font-semibold text-surface-600">Filter manager list:</span>
            <label className="flex items-center gap-1.5 text-xs text-surface-700">
              <Checkbox checked={sameOfficeOnly} onCheckedChange={(v) => setSameOfficeOnly(v === true)} disabled={!officeId} />
              Same office only
            </label>
            <label className="flex items-center gap-1.5 text-xs text-surface-700">
              <Checkbox checked={managersOnly} onCheckedChange={(v) => setManagersOnly(v === true)} />
              Manager tier only
            </label>
          </div>
        )}
        {canView("managerId") && (
          <Field label="HR Manager">
            <SelectField {...inputProps("managerId", managerId, setManagerId)}>
              <option value="">No HR manager</option>
              {hrManagerOptions.map((m) => <option key={m.id} value={m.id}>{m.firstName} {m.lastName} ({m.employeeCode})</option>)}
            </SelectField>
          </Field>
        )}
        {canView("reportingManagerId") && (
          <Field label="Reporting Manager">
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={sameAsHR}
                  onChange={e => setSameAsHR(e.target.checked)}
                />
                Same as HR Manager
              </label>
              <SelectField
                value={sameAsHR ? managerId : reportingManagerId}
                onChange={e => { setSameAsHR(false); setReportingManagerId(e.target.value); }}
                disabled={sameAsHR}
              >
                <option value="">No reporting manager</option>
                {reportingManagerOptions.map((m) => <option key={m.id} value={m.id}>{m.firstName} {m.lastName} ({m.employeeCode})</option>)}
              </SelectField>
            </div>
          </Field>
        )}
        {canView("workLocation") && (
          <Field label="Work Location">
            <SelectField {...inputProps("workLocation", workLocation, setWorkLocation)}>
              {workLocations.map((o) => <option key={o.value} value={o.value}>{o.displayName}</option>)}
            </SelectField>
          </Field>
        )}
        {canView("hireDate") && (
          <Field label="Hire Date *">
            <DatePicker {...datePickerProps("hireDate", hireDate, setHireDate)} />
          </Field>
        )}
        {canView("probationEndDate") && (
          <Field label="Probation End Date">
            <DatePicker {...datePickerProps("probationEndDate", probationEndDate, setProbationEndDate)} />
          </Field>
        )}
      </FieldGrid>
      {/* Offer Letter — edit mode only. On create it is one of the two intake
          slots at the top of this card, held until the save like before. */}
      {!isCreate && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-surface-200 bg-surface-50 px-3.5 py-2.5">
          <span className="min-w-0 flex-1 text-[13px] font-semibold">Offer Letter</span>
          <UploadZone
            label="Upload Offer Letter"
            accept="image/*,.pdf,.doc,.docx"
            onFile={async (f) => {
              if (!employeeId) return;
              // HRMS-0633: run the AI on re-upload. The parent uses the
              // extracted annual CTC to prefill the compensation drawer.
              // Extraction failures stay non-fatal — the file is still stored.
              let fields: Record<string, unknown> | null = null;
              try {
                fields = await uploadDocumentWithAI(employeeId, f, "OFFER_LETTER", "OFFER_LETTER") as Record<string, unknown>;
              } catch { fields = null; }
              if (fields && onOfferLetterParsed) onOfferLetterParsed(fields);
              bumpDocRefresh("OFFER_LETTER");
            }}
          />
          {employeeId && (
            <DocVersionHistory employeeId={employeeId} docType="OFFER_LETTER" isHR={isHR} refreshKey={docRefreshKeys["OFFER_LETTER"] ?? 0} onVerify={setVerifyTarget} />
          )}
        </div>
      )}

      {/* Create the hire, then send the link — both from this card. */}
      {intake && !intake.created && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Tooltip content={intake.missing.length > 0 ? `Still needed: ${intake.missing.join(", ")}` : null}>
            {/* Wrapper keeps the tooltip working while the button is disabled. */}
            <span className="inline-flex">
              <AsyncButton onClick={() => intake.onSave()} disabled={intake.missing.length > 0}>
                Save &amp; create hire
              </AsyncButton>
            </span>
          </Tooltip>
          <span className="text-[11px] text-surface-500">
            You can fill in the rest of the sections now or after the employee completes onboarding.
          </span>
        </div>
      )}
      {intake?.created && (
        <NewHireCreatedPanel
          employeeId={intake.created.employeeId}
          employeeCode={intake.created.employeeCode}
          name={`${firstName} ${lastName}`.trim()}
          personalEmail={intake.personalEmail}
          record={intake.created.record}
          onLinkSent={intake.created.onLinkSent}
          onOpenProfile={intake.created.onOpenProfile}
        />
      )}
    </CollapsibleSection>
  );
}
